import React, { useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const Header = ({ merchantId, locationNr, onLocationChange }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [locationInput, setLocationInput] = useState(locationNr || '*')
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Update locationInput when locationNr prop changes
  useEffect(() => {
    setLocationInput(locationNr || '*')
  }, [locationNr])

  const toggleMenu = () => {
    setMenuOpen(!menuOpen)
  }

  // This function updates the location without page reloads
  const updateLocation = useCallback((newLocation) => {
    // Get current merchant ID
    const currentMerchantId = merchantId || searchParams.get('mid') || searchParams.get('merchant_id')

    if (!currentMerchantId) {
      console.error("No merchant ID found for location update")
      return
    }

    // For HashRouter, we need to update the hash part only, not the URL params
    // This prevents duplicate parameters in the URL
    let path = '/'
    let params = {}

    // Add merchant ID
    params.mid = currentMerchantId

    // Add location if it's not the default
    if (newLocation !== '*') {
      params.loc = newLocation
    }

    // Create the search string for the hash
    const searchString = new URLSearchParams(params).toString()

    // Navigate using hash only, with empty URL search part
    navigate({
      pathname: path,
      search: '', // Important: Clear URL search params to avoid duplication
      hash: searchString ? `?${searchString}` : ''
    })

    // Update local state
    setLocationInput(newLocation)

    // Notify parent component
    if (onLocationChange) {
      onLocationChange(newLocation === '*' ? null : newLocation)
    }

    // Dispatch event
    window.dispatchEvent(new CustomEvent('locationChanged', {
      detail: { locationNr: newLocation === '*' ? null : newLocation }
    }))
  }, [merchantId, navigate, onLocationChange, searchParams])

  const handlePrevLocation = () => {
    if (locationInput === '*') {
      updateLocation('9')
    } else {
      const current = parseInt(locationInput, 10)
      if (current <= 1) {
        updateLocation('*')
      } else {
        updateLocation(String(current - 1))
      }
    }
  }

  const handleNextLocation = () => {
    if (locationInput === '*') {
      updateLocation('1')
    } else {
      const current = parseInt(locationInput, 10)
      if (current >= 9) {
        updateLocation('*')
      } else {
        updateLocation(String(current + 1))
      }
    }
  }

  const handleLocationInputChange = (e) => {
    const value = e.target.value
    setLocationInput(value)
  }

  const handleLocationInputBlur = () => {
    // Validate and apply the location input
    if (locationInput === '' || locationInput === '*') {
      updateLocation('*')
    } else {
      const numValue = parseInt(locationInput, 10)
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 9) {
        updateLocation(String(numValue))
      } else {
        // Reset to previous valid value
        setLocationInput(locationNr || '*')
      }
    }
  }

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    }
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <SafeIcon icon={FiIcons.FiClipboard} className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">Bike Return Dashboard</span>
            </Link>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-6">
            {/* Location Navigation Controls */}
            <div className="flex items-center space-x-2 mr-4">
              <span className="text-sm text-gray-600">Location:</span>
              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                <button
                  onClick={handlePrevLocation}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 border-r border-gray-300"
                  aria-label="Previous location"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <input
                  type="text"
                  value={locationInput}
                  onChange={handleLocationInputChange}
                  onBlur={handleLocationInputBlur}
                  onKeyDown={handleLocationKeyDown}
                  className="w-8 text-center border-none focus:ring-0 focus:outline-none text-sm"
                  aria-label="Location number"
                />
                <button
                  onClick={handleNextLocation}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 border-l border-gray-300"
                  aria-label="Next location"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center md:hidden">
            {/* Mobile location navigation */}
            <div className="flex items-center space-x-1 mr-2">
              <button
                onClick={handlePrevLocation}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded-md"
                aria-label="Previous location"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="text"
                value={locationInput}
                onChange={handleLocationInputChange}
                onBlur={handleLocationInputBlur}
                onKeyDown={handleLocationKeyDown}
                className="w-6 text-center border-none focus:ring-0 focus:outline-none text-xs bg-gray-100 rounded-md"
                aria-label="Location number"
              />
              <button
                onClick={handleNextLocation}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded-md"
                aria-label="Next location"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={toggleMenu}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {menuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1 px-4">
            {/* Mobile menu content can go here if needed */}
          </div>
        </div>
      )}
    </header>
  )
}

export default Header